import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { SitesService } from '../../services/sites/sites.service';
import { debounceTime, Subject } from 'rxjs';
import { SECOND } from '@shared/utils/data-utils';
import { DateRange } from '@shared/utils/date-range';
import { RangePreset } from '@shared/services/calendar-data.service';
import moment from 'moment/moment';
import { ADMIN_CONTEXT, ContextEnum, ContextSiteModel } from '@shared/models/ContextSiteModel';
import { arrayEquals } from '@shared/utils/lang';

import { splitClipboard } from '@shared/utils/clipboard';
import { NgSelectComponent } from '@ng-select/ng-select';
import { FormsModule } from '@angular/forms';
import { NgIf, NgStyle } from '@angular/common';
import { FeatureFlagDirective } from '../../utils/feature-flag.directive';
import { DateRangeSelectorDirective } from '../../directives/date-range-selector.directive';
import { SwitchComponent } from '../switch/switch.component';
import { DurationSecondsPipe } from '../../pipe/filter.pipe';

const REFRESH_DURATION = 60 * SECOND;
const COUNTDOWN_STEP_DURATION = 1 * SECOND;

@Component({
  selector: 'app-site-selector',
  templateUrl: './site-selector.component.html',
  styleUrls: ['./site-selector.component.scss'],
  imports: [
    NgSelectComponent,
    FormsModule,
    NgIf,
    FeatureFlagDirective,
    DateRangeSelectorDirective,
    NgStyle,
    SwitchComponent,
    DurationSecondsPipe,
    TranslatePipe,
  ],
})
export class SiteSelectorComponent implements AfterViewInit, OnDestroy {
  @Input() ctx: ContextEnum;
  @Input() showTags = true;
  @Input() showOwner = false;
  @Input() canSelectAllClusters = false;
  @Input() showDatePicker = true;
  @Input() canAutoRefresh = false;
  @Input() isLoading = false;
  @Input() datePickerSlug = ''; // have to pass a different HTML id to each date range picker between each page, otherwise it's buggy
  @Input() minDateDays = 90;
  @Input() maxSpan = moment.duration(90, 'days');
  @Input() ranges: RangePreset[] = ['15M', '1H', '4H', 'T', 'Y', '7d', '30d', '90d'];
  @Output() refresh = new EventEmitter();
  @Output() selectionChange = new EventEmitter<SiteSelection>();
  @Output() onPeriodEmitted = new EventEmitter<DateRange>();

  refreshDuration = REFRESH_DURATION;
  refreshInterval: any = null;
  countdownProgressInterval: any = null;
  countNextRefresh = 0;
  countSteps = this.refreshDuration / COUNTDOWN_STEP_DURATION;

  private debounceSubject: Subject<SiteSelection> = new Subject();
  private lastEmittedSelection?: SiteSelection;

  public ADMIN_CONTEXT = ADMIN_CONTEXT;
  protected readonly Math = Math;

  constructor(
    public translate: TranslateService,
    public sites: SitesService,
  ) {}

  ngAfterViewInit(): void {
    this.debounceSubject.pipe(debounceTime(500)).subscribe((selection) => {
      if (this.lastEmittedSelection && this.sameSelection(selection, this.lastEmittedSelection)) {
        return;
      }
      this.lastEmittedSelection = selection;
      this.selectionChange.emit(selection);
    });
  }

  ngOnDestroy(): void {
    this.clearRefreshInterval();
  }

  onSitesOpen(): void {
    this.lastEmittedSelection = this.currentSelection();
  }

  setRefreshInterval() {
    if (this.refreshInterval) {
      return;
    }

    this.countNextRefresh = 0;
    this.countdownProgressInterval = setInterval(() => this.countNextRefresh++, COUNTDOWN_STEP_DURATION);
    this.refreshInterval = setInterval(() => this.onRefresh(), REFRESH_DURATION + COUNTDOWN_STEP_DURATION);
  }

  clearRefreshInterval() {
    if (!this.refreshInterval) {
      return;
    }

    clearInterval(this.countdownProgressInterval);
    clearInterval(this.refreshInterval);
    this.refreshInterval = null;
    this.countNextRefresh = 0;
  }

  onRefresh() {
    this.clearRefreshInterval();
    this.refresh.emit();
  }

  onDataLoaded() {
    if (this.sites[this.ctx].current.period.autoRefresh && this.canAutoRefresh) this.setRefreshInterval();
  }

  onSelectionRemoved(event) {
    this.lastEmittedSelection = undefined;
    this.hostChange(event);
  }
  refreshSiteList(event) {
    this.sites.computeSiteList(this.ctx);
    this.hostChange(event);
  }

  hostChange(event) {
    this.clearRefreshInterval();
    this.debounceSubject.next(this.currentSelection());
  }

  rangeChange(range: DateRange) {
    this.clearRefreshInterval();
    this.onPeriodEmitted.emit(range);
  }

  private currentSelection(): SiteSelection {
    return {
      clusterUid: this.sites[this.ctx].current.cluster?.uid,
      sites: this.sites[this.ctx].current.selectedSites?.map((site) => site.domainName),
      ...(this.showTags ? { tags: this.sites[this.ctx].current.tag ? [this.sites[this.ctx].current.tag] : [] } : {}),
    };
  }
  sameSelection(a: SiteSelection, b: SiteSelection): boolean {
    return a.clusterUid === b.clusterUid && arrayEquals(a.sites, b.sites) && arrayEquals(a.tags, b.tags);
  }

  onPaste($event: ClipboardEvent) {
    console.log($event);
    let filteredSites = splitClipboard($event)
      .filter((val) => this.isSiteSelectable(val))
      .map((val) => this.findSite(val));

    this.sites[this.ctx].current.selectedSites = Array.from(
      new Set(this.sites[this.ctx].current.selectedSites.concat(filteredSites)),
    );
  }

  private findSite(domain: string): ContextSiteModel {
    return this.sites[this.ctx].store.sitesList.find((siteContext) => domain === siteContext.domainName);
  }
  private isSiteSelectable(domain: string): boolean {
    return this.sites[this.ctx].store.sites.some((site) => site.domainName === domain);
  }
}
type SiteSelection = {
  clusterUid: string;
  sites: string[];
  tags?: string[];
};
